import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {MatButton} from '@angular/material/button';
import {AvlModel, VehicleModel} from '../../../../../../shared/models/vehicle.model';
import {CameraConfigurationComponent} from '../camera-configuration/camera-configuration.component';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {VehiclesService} from '../../../../../../data/vehicles/vehicles.service';
import {firstValueFrom, Subscription} from 'rxjs';
import {MatError} from '@angular/material/form-field';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {
  DigitalSpreaderConfigurationComponent
} from '../digital-spreader-configuration/digital-spreader-configuration.component';
import {VehicleControlUnit, VehicleHardware, VehicleHardwareType} from '../model/plowops-pro-models';
import {MatProgressBar} from '@angular/material/progress-bar';
import {PlowConfigurationComponent} from '../plow-configuration/plow-configuration.component';
import {
  InputConnectionConfigurationComponent
} from '../input-connection-configuration/input-connection-configuration.component';
import {SpreaderConfigurationComponent} from '../spreader-configuration/spreader-configuration.component';
import {ConfigurationService} from '../../../../../../configuration/configuration.service';
import {ConfigurationModel, FeatureFlagEnum} from '../../../../../../shared/models/configuration.model';

@Component({
  selector: 'app-edit-vehicle-hardware-dialog',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    CameraConfigurationComponent,
    ReactiveFormsModule,
    MatError,
    MatProgressSpinner,
    DigitalSpreaderConfigurationComponent,
    MatProgressBar,
    PlowConfigurationComponent,
    InputConnectionConfigurationComponent,
    SpreaderConfigurationComponent
  ],
  styleUrls: [
    '../../../../settings-common.scss'
  ],
  templateUrl: './edit-vehicle-hardware-dialog.component.html',
})
export class EditVehicleHardwareDialogComponent implements OnInit, OnDestroy {
  isLoading = false;

  vehicle: VehicleModel;
  vcu: VehicleControlUnit;

  editedType: EditVehicleType;
  editedHardware?: VehicleHardware;

  isEdit = false;

  formGroup: FormGroup;

  isSaving = false;

  private configuration: ConfigurationModel;

  hardwareTypeToAdd: VehicleHardwareChoice[] = [];
  selectedHardware?: VehicleHardwareChoice;

  saveError: any;

  subscriptions: Subscription[] = [];

  protected readonly vehicleHardwareTypeLabels = vehicleHardwareChoiceLabels;
  protected readonly VehicleHardwareChoice = VehicleHardwareChoice;

  constructor(@Inject(MAT_DIALOG_DATA) private readonly data: EditVehicleHardwareDialogData,
              private readonly vehicleService: VehiclesService,
              private readonly configurationService: ConfigurationService,
              private readonly dialogRef: MatDialogRef<any>,
              private readonly fb: FormBuilder,
  ) {
    this.subscriptions.push(
      this.configurationService.sharedConfigurationModel
        .subscribe(configuration => {
          this.configuration = configuration;
        })
    );

    this.editedType = data.editedType;
    this.isEdit = !!this.editedType;

    this.formGroup = this.fb.group({
      cameraConfiguration: this.fb.control(null),
      digitalSpreader: this.fb.control(null),
      plow: this.fb.control(null),
      spreader: this.fb.control(null),
      genericDevice: this.fb.control(null),
    });

    this.subscriptions.push(
      this.formGroup.valueChanges.pipe().subscribe(value => {
        const {cameraConfiguration, digitalSpreader, plow, spreader, genericDevice} = value;
        switch (this.selectedHardware) {
          case VehicleHardwareChoice.CAMERA:
            this.vcu.cameraConfiguration = cameraConfiguration;
            break;
          case VehicleHardwareChoice.DIGITAL_SPREADER:
            this.vcu.digitalSpreader = digitalSpreader;
            break;
          case VehicleHardwareChoice.PLOW:
            this.updateOrAddAdditionalHardware(plow);
            break;
          case VehicleHardwareChoice.SPREADER: {
            this.updateOrAddAdditionalHardware(spreader);
            break;
          }
          case VehicleHardwareChoice.SWEEPER:
          case VehicleHardwareChoice.MOWER: {
            const hardwareConfiguration = {
              connection: genericDevice,
              type: this.selectedHardware === VehicleHardwareChoice.SWEEPER
                ? VehicleHardwareType.SWEEPER
                : VehicleHardwareType.MOWER
            } as VehicleHardware;
            this.updateOrAddAdditionalHardware(hardwareConfiguration);
            break;
          }
        }
      })
    );
  }

  ngOnInit() {
    this.loadVehicleData(this.data.vehicleId)
      .then(vehicle => {
        this.vcu = VehicleControlUnit.fromVehicleModel(vehicle);
        if (this.editedType) {
          this.setEditedHardwareData();
        } else {
          this.setHardwareChoicesData();
        }
      });
  }

  private setHardwareChoicesData() {
    if (this.configuration.hasFeatureFlag(FeatureFlagEnum.DashCam) && !this.vcu?.cameraConfiguration) {
      this.hardwareTypeToAdd.push(VehicleHardwareChoice.CAMERA);
    }
    if (!this.vcu?.digitalSpreader) {
      this.hardwareTypeToAdd.push(VehicleHardwareChoice.DIGITAL_SPREADER);
    }
    if (this.vcu.avlModel !== AvlModel.NONE) {
      this.hardwareTypeToAdd.push(VehicleHardwareChoice.SPREADER);
      this.hardwareTypeToAdd.push(VehicleHardwareChoice.PLOW);
      this.hardwareTypeToAdd.push(VehicleHardwareChoice.SWEEPER);
      this.hardwareTypeToAdd.push(VehicleHardwareChoice.MOWER);
    }
  }

  private setEditedHardwareData() {
    const {cameraConfiguration, digitalSpreader} = this.vcu;
    if (this.editedType === EditVehicleType.OTHER) {
      this.editedHardware = this.vcu.additionalHardware[this.data.editedIndex];
    }

    this.formGroup.setValue({
      cameraConfiguration: cameraConfiguration || null,
      digitalSpreader: digitalSpreader || null,
      plow: this.editedHardware || null,
      spreader: this.editedHardware || null,
      genericDevice: this.editedHardware?.connection || null,
    }, {
      emitEvent: false
    });

    if (this.editedType === EditVehicleType.OTHER) {
      this.selectedAddedHardwareType({
        [VehicleHardwareType.PLOW]: this.VehicleHardwareChoice.PLOW,
        [VehicleHardwareType.GRANULAR_SPREADER]: this.VehicleHardwareChoice.SPREADER,
        [VehicleHardwareType.LIQUID_SPREADER]: this.VehicleHardwareChoice.SPREADER,
        [VehicleHardwareType.SWEEPER]: this.VehicleHardwareChoice.SWEEPER,
        [VehicleHardwareType.MOWER]: this.VehicleHardwareChoice.MOWER,
      }[this.editedHardware.type]);
    } else {
      this.selectedAddedHardwareType({
        [EditVehicleType.CAMERA]: VehicleHardwareChoice.CAMERA,
        [EditVehicleType.DIGITAL_SPREADER]: VehicleHardwareChoice.DIGITAL_SPREADER,
      }[this.editedType]);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  updateVehicleHardware() {
    const vehicleHardware = this.vcu.toVehicleModel();
    const updatedVehicle = {...this.vehicle, ...vehicleHardware};

    this.isSaving = true;
    firstValueFrom(this.vehicleService.updateVehicle(this.vehicle.id, updatedVehicle)).then(response => {
      this.dialogRef.close(response.data);
    }).catch(error => {
      this.saveError = error;
    }).finally(() => {
      this.isSaving = false;
    });
  }

  selectedAddedHardwareType(type: VehicleHardwareChoice) {
    this.selectedHardware = type;
    const {cameraConfiguration, digitalSpreader, plow, spreader, genericDevice} = this.formGroup.controls;
    if (type !== VehicleHardwareChoice.CAMERA) {
      cameraConfiguration.disable({emitEvent: false});
    }
    if (type !== VehicleHardwareChoice.DIGITAL_SPREADER) {
      digitalSpreader.disable({emitEvent: false});
    }
    if (type !== VehicleHardwareChoice.PLOW) {
      plow.disable({emitEvent: false});
    }
    if (type !== VehicleHardwareChoice.SPREADER) {
      spreader.disable({emitEvent: false});
    }
    if (![VehicleHardwareChoice.SWEEPER, VehicleHardwareChoice.MOWER, VehicleHardwareChoice.SWEEPER].includes(type)) {
      genericDevice.disable({emitEvent: false});
    }

    // skip config dialog (auto-add) for camera hardware
    if (this.selectedHardware === VehicleHardwareChoice.CAMERA) {
      setTimeout(
        () => this.updateVehicleHardware(),
        100,
      );
    }
  }

  private updateOrAddAdditionalHardware(hardware: VehicleHardware) {
    if (this.data.editedIndex !== undefined) {
      this.vcu.additionalHardware[this.data.editedIndex] = hardware;
    } else {
      this.vcu.additionalHardware.push(hardware);
      this.data.editedIndex = this.vcu.additionalHardware.indexOf(hardware);
    }
  }

  private async loadVehicleData(id: number) {
    this.isLoading = true;
    this.vehicle = await this.vehicleService.getVehicle(id);
    this.isLoading = false;
    return this.vehicle;
  }

  getAvailableInputs() {
    return this.vcu.getAvailableInputs(this.data.editedIndex);
  }
}

export interface EditVehicleHardwareDialogData {
  vehicleId: number;

  editedType?: EditVehicleType;
  editedIndex?: number;
}

export enum EditVehicleType {
  CAMERA = 'CAMERA',
  DIGITAL_SPREADER = 'DIGITAL_SPREADER',
  OTHER = 'OTHER'
}

export enum VehicleHardwareChoice {
  CAMERA = 'CAMERA',
  DIGITAL_SPREADER = 'DIGITAL_SPREADER',
  SPREADER = 'SPREADER',
  PLOW = 'PLOW',
  SWEEPER = 'SWEEPER',
  MOWER = 'MOWER',
}

export const vehicleHardwareChoiceLabels = {
  [VehicleHardwareChoice.CAMERA]: 'Camera',
  [VehicleHardwareChoice.DIGITAL_SPREADER]: 'Digital Spreader',
  [VehicleHardwareChoice.SPREADER]: 'Spreader',
  [VehicleHardwareChoice.PLOW]: 'Plow',
  [VehicleHardwareChoice.SWEEPER]: 'Sweeper',
  [VehicleHardwareChoice.MOWER]: 'Mower',
};
